import React from "react";
import {
  Page as PdfPage,
  Document,
  StyleSheet,
  View,
  Text,
  Image,
} from "@react-pdf/renderer";
import moment from "moment";
import { formatDistance, parseISO } from "date-fns";
import fulllogo from "../../images/fulllogo.png"; // Import the logo - adjust the path as needed

// Create styles
const styles = StyleSheet.create({
  logoImage: {
    width: 250, // Adjust width as needed
    marginBottom: 35,
  },
  page: {
    paddingLeft: 100,
    paddingRight: 100,
    paddingTop: 50,
    paddingBottom: 50,
    fontFamily: "Helvetica",
  },
  header: {
    marginBottom: 20,
    textAlign: "left",
  },
  logo: {
    fontSize: 28,
    fontWeight: "bold",
    marginBottom: 5,
  },
  subtitle: {
    fontSize: 24,
    marginBottom: 10,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 5,
    // textDecoration: "underline",
    textAlign: "left",
  },
  infoRow: {
    flexDirection: "row",
    marginBottom: 35,
    fontSize: 18,
    alignItems: "center",
    justifyContent: "space-between",
  },
  label: {
    fontWeight: "bold",
    color: "#1f7e77",
    fontSize: 18,
  },
  value: {
    fontWeight: "bold",
  },
  pill: {
    backgroundColor: "#1f7e77",
    fontWeight: "bold",
    padding: 7,
    borderRadius: "15px",
    color: "#fff",
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 15,
    backgroundColor: "#1f7e77",
    padding: 10,
    color: "#fff",
  },
  subsectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 20,
    marginTop: 25,
  },
  subsectionTitleGreen: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 15,
    color: "#1f7e77",
  },
  table: {
    width: "200%",
    marginBottom: 15,
    // borderStyle: "solid",
    // borderWidth: 1,
    // borderRightWidth: 0,
    // borderBottomWidth: 0,
  },
  tableRow: {
    flexDirection: "row",
  },
  tableColHeader: {
    // width: "25%",
    // borderStyle: "solid",
    // borderBottomWidth: 1,
    // borderRightWidth: 1,
    padding: 5,
    // backgroundColor: "#f0f0f0",
    fontWeight: "bold",
    fontSize: 18,
    color: "#1f7e77",
  },
  tableCol: {
    // width: "25%",
    // borderStyle: "solid",
    // borderBottomWidth: 1,
    // borderRightWidth: 1,
    padding: 5,
    fontSize: 18,
    marginRight: "60px",
  },
  listItem: {
    fontSize: 18,
    marginBottom: 3,
    marginLeft: 10,
  },
  vitalCard: {
    width: "33%",
    marginBottom: 15,
  },
  vitalTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 3,
  },
  vitalValue: {
    fontSize: 18,
    marginBottom: 5,
  },
  chart: {
    width: "100%",
    height: 260,
    marginBottom: 30,
  },
});

const Header = () => (
  <View style={styles.header} fixed>  {/* fixed makes it appear on every page */}
    <Image src={fulllogo} style={styles.logoImage} />
  </View>
);

const PageWithHeader = ({ children, ...props }) => (
  <PdfPage {...props}>
    <Header />
    <View style={styles.content}>
      {children}
    </View>
  </PdfPage>
);
const SessionNotePDF = ({
  chartImage1,
  chartImage2,
  chartImage3,
  chartImage4,
  chartImage5,
  session,
}) => {
  if (!session) {
    return null;
  }

  const stripHtml = (html) => {
    if (!html) return "";

    const items =
      html
        .match(/<li>(.*?)<\/li>/g)
        ?.map((item) => item.replace(/<\/?li>/g, "").trim()) || [];

    const hasUL = html.includes("<ul>") && html.includes("<li>");

    if (hasUL) {
      return items.map((item, index) => <Text key={index}>• {item}</Text>);
    } else if (html.includes('<p>')) {
      const paragraphs = html.match(/<p>(.*?)<\/p>/g)?.map(p => {
        let clean = p.replace(/<\/?p>/g, '').replace(/&nbsp;/g, ' ');
        clean = clean.replace(/[ÕÐÍº]/g, '-');
        return clean.trim();
      }) || [];
      return paragraphs.map((para, index) => (
        <Text key={index} >{para}</Text>
      ));
    } else {
      return <Text>{html}</Text>;
    }
  };

  return (
    <Document>
        <PageWithHeader 
        size={[1270.8, 1812.24]} 
        style={styles.page}
      >
      {/* <Page size={[1270.8, 1812.24]} style={styles.page}> */}
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Individual Session Note</Text>
        </View>

        {/* Patient Information */}
        <View style={styles.infoRow}>
          <View style={{ display: "flex", flexDirection: "row", gap: 10 }}>
            <Text style={styles.label}>Name</Text>
            <Text style={styles.value}>{session?.user[0]?.full_name}</Text>
          </View>
          <View style={{ display: "flex", flexDirection: "row", gap: 10 }}>
            <Text style={styles.label}>Age</Text>
            <Text style={styles.value}>
              {moment().diff(new Date(session?.user[0]?.dob), "years")} Years
            </Text>
          </View>
          <View style={{ display: "flex", flexDirection: "row", gap: 10 }}>
            <Text style={styles.label}>Gender</Text>
            <Text style={styles.value}>{session?.user[0]?.gender}</Text>
          </View>
        </View>

        <View style={styles.infoRow}>
          <View style={{ display: "flex", flexDirection: "row", gap: 10 }}>
            <Text style={styles.label}>Encounter type</Text>
            <Text style={styles.value}>{session?.session.title}</Text>
          </View>
          <View style={{ display: "flex", flexDirection: "row", gap: 10 }}>
            <Text style={styles.label}>Encounter Date</Text>
            <Text style={styles.value}>
              {moment(session?.session.sessionDate).format("DD MMMM, YYYY")}
            </Text>
          </View>
          <View style={{ display: "flex", flexDirection: "row", gap: 10 }}>
            <Text style={styles.label}>Duration</Text>
            <Text style={styles.value}>
              {
                Math.floor((new Date(session?.session?.endTime) - new Date(session?.session?.startTime)) / 60000)+" minutes"
              }
            </Text>
          </View>
        </View>

        <View style={styles.infoRow}>
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 10,
              alignItems: "center",
            }}
          >
            <Text style={styles.label}>Health Risk</Text>
            <Text style={styles.pill}>
            {             
              session?.userAssesment[0]?.totalScore <= 120 ?
                  "Mild Risk" :
                  session?.userAssesment[0]?.totalScore > 120 && session?.userAssesment[0]?.totalScore <= 240 ?
                    "Moderate Risk" : session?.userAssesment[0]?.totalScore > 240 && session?.userAssesment[0]?.totalScore <= 360 ?
                      "High Risk" : "None"
                      
              }
            </Text>
          </View>
        </View>

        <View style={styles.infoRow}>
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 10,
              alignItems: "center",
            }}
          >
            <Text style={styles.label}>Allergies</Text>
            {session?.userSession?.allergies?.length > 0 ? (
              session?.userSession?.allergies?.map((item, index) => (
                <Text key={index} style={styles.pill}>
                  {item}
                </Text>
              ))
            ) : (
              <Text style={styles.value}>None</Text>
            )}
          </View>
        </View>

        <View style={styles.infoRow}>
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              gap: 10,
              alignItems: "center",
            }}
          >
            <Text style={styles.label}>Current Medications</Text>
            {session?.userSession?.medications?.length > 0 ? (
              session?.userSession?.medications?.map((item, index) => (
                <Text key={index} style={styles.pill}>
                  {item.name}
                </Text>
              ))
            ) : (
              <Text style={styles.value}>None</Text>
            )}
          </View>
        </View>

        {/* Subjective Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Subjective</Text>

          <View
            style={{ flexDirection: "row", justifyContent: "space-between" }}
          >
            <Text style={styles.subsectionTitleGreen}>Chief Complaints</Text>
            <View style={{ width: "62%" }}>
              {session?.userSession?.chiefComplaints?.map((item, index) => (
                <View key={index} style={{ marginBottom: 25 }}>
                  <Text style={{ fontSize: 18, backgroundColor: "#fcfcfc" }}>
                    {item.complaints}
                  </Text>
                </View>
              ))}
            </View>
            <Text style={styles.subsectionTitleGreen}>Duration</Text>
            <View>
              {session?.userSession?.chiefComplaints?.map((item, index) => (
                <View key={index} style={{ marginBottom: 25 }}>
                  <Text style={{ fontSize: 16, backgroundColor: "#fcfcfc" }}>
                    {item.duration} {item.frequency}
                  </Text>
                </View>
              ))}
            </View>
          </View>

          <Text style={styles.subsectionTitle}>History of Present Illness</Text>
          <View style={{ flexDirection: "column", gap: 10 }}>
            {stripHtml(session?.userSession?.historyOfPresentIllness)}
          </View>

          <Text style={styles.subsectionTitle}>Past Medical History</Text>
          <View>
            {session?.userSession?.pastMedicalHistory?.map((item, index) => (
              <Text key={index} style={styles.listItem}>
                • {item.medicalCondition} (diagnosed {item.duration}{" "}
                {item.frequency} ago)
              </Text>
            ))}
          </View>

          <Text style={styles.subsectionTitle}>Family & Social History</Text>

          <View style={styles.table}>
            {session?.userSession?.familyMedicalHistory?.history?.map(
              (item, index) => (
                <View key={index} style={styles.tableRow}>
                  {/* Relationship */}
                  <View style={styles.tableColHeader}>
                    <Text>Relationship</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text>{item.relation}</Text>
                  </View>

                  {/* Disease Condition */}
                  <View style={styles.tableColHeader}>
                    <Text>Disease Condition</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text>{item.medicalCondition}</Text>
                  </View>

                  {/* Duration */}
                  <View style={styles.tableColHeader}>
                    <Text>Duration</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <View style={styles.tableCol}>
                      <Text>
                        {item.duration} {item.frequency}
                      </Text>
                    </View>
                  </View>

                  {/* Cause of Death */}
                  <View style={styles.tableColHeader}>
                    <Text>Cause of Death</Text>
                  </View>
                  <View style={styles.tableCol}>
                    <Text>{item.causeOfDeath}</Text>
                  </View>
                </View>
              )
            )}
          </View>

          <View style={{ ...styles.infoRow, width: "95%" }}>
            <View style={{ display: "flex", flexDirection: "row", gap: 60 }}>
              <Text style={{}}>Smoking</Text>
              <Text style={{}}>
                {session?.userSession?.familyMedicalHistory?.smoking}
              </Text>
            </View>

            <View style={{ display: "flex", flexDirection: "row", gap: 60 }}>
              <Text style={{}}>Alcohol</Text>
              <Text style={{}}>
                {session?.userSession?.familyMedicalHistory?.alcohol}
              </Text>
            </View>

            <View style={{ display: "flex", flexDirection: "row", gap: 60 }}>
              <Text style={{}}>Exercise</Text>
              <Text style={{}}>
                {session?.userSession?.familyMedicalHistory?.exercise}
              </Text>
            </View>
          </View>
          <View style={{ flexDirection: "column", gap: 10 }}>
            {stripHtml(session?.userSession?.familyMedicalHistory?.comments)}
          </View>
          <Text style={styles.subsectionTitle}>Review of Systems</Text>
          <View style={{ flexDirection: "column", gap: 10 }}>
            {stripHtml(session?.userSession?.reviewOfSystems)}
          </View>
        </View>

        {/* Objective Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Objective</Text>
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "space-between",
            }}
          >
            <View style={{ width: "30%" }}>
              <Text style={styles.subsectionTitle}>Blood Pressure</Text>
              <View
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 10,
                  marginBottom: 20,
                }}
              >
                <Text style={{}}>
                  {session?.userSession?.bloodPressure?.systolic}
                </Text>
                <Text style={styles.label}> / </Text>
                <Text style={{}}>
                  {" "}
                  {session?.userSession?.bloodPressure?.diastolic}
                </Text>
                <Text style={styles.label}>mmHg</Text>
              </View>
              {chartImage1 && <Image style={styles.chart} src={chartImage1} />}
              <Text style={styles.label}>Last 5 documented readings</Text>
            </View>
            <View style={{ width: "30%" }}>
              <Text style={styles.subsectionTitle}>Respiratory Rate</Text>
              <View
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 10,
                  marginBottom: 20,
                }}
              >
                <Text style={{}}>{session?.userSession?.respiratoryRate}</Text>
                <Text style={styles.label}>cycles/min</Text>
              </View>
              {chartImage2 && <Image style={styles.chart} src={chartImage2} />}
              <Text style={styles.label}>Last 5 documented readings</Text>
            </View>
            <View style={{ width: "30%" }}>
              <Text style={styles.subsectionTitle}>Temperature</Text>
              <View
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 10,
                  marginBottom: 20,
                }}
              >
                <Text style={{}}>{session?.userSession?.temperature}</Text>
                <Text style={styles.label}>°F</Text>
              </View>
              {chartImage3 && <Image style={styles.chart} src={chartImage3} />}
              <Text style={styles.label}>Last 5 documented readings</Text>
            </View>
          </View>
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "space-evenly",
            }}
          >
            <View style={{ width: "30%" }}>
              <Text style={styles.subsectionTitle}>Heart Rate</Text>
              <View
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 10,
                  marginBottom: 20,
                }}
              >
                <Text style={{}}>{session?.userSession?.heartRate}</Text>
                <Text style={styles.label}>beats/min</Text>
              </View>
              {chartImage4 && <Image style={styles.chart} src={chartImage4} />}
              <Text style={styles.label}>Last 5 documented readings</Text>
            </View>
            <View style={{ width: "30%" }}>
              <Text style={styles.subsectionTitle}>Oxygen Saturation</Text>
              <View
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 10,
                  marginBottom: 20,
                }}
              >
                <Text style={{}}>{session?.userSession?.oxygenSaturation}</Text>
                <Text style={styles.label}>%</Text>
              </View>
              {chartImage5 && <Image style={styles.chart} src={chartImage5} />}
              <Text style={styles.label}>Last 5 documented readings</Text>
            </View>
          </View>

          <Text style={styles.subsectionTitle}>Other System Comments</Text>
          <View style={{ flexDirection: "column", gap: 10 }}>
            {stripHtml(session?.userSession?.otherSystemComments)}
          </View>
        </View>

        {/* Assessment Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Assessment</Text>
          <View style={{ marginBottom: 20 }}>
            <Text style={styles.subsectionTitle}>
              {session?.userSession?.assessmentDiagnosis}
            </Text>
          </View>
          <View style={{ flexDirection: "column", gap: 10 }}>
            {stripHtml(session?.userSession?.assessmentComments)}
          </View>
        </View>

        {/* Plan Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Plan</Text>
          <View style={{ marginBottom: 20 }}>
            {session?.userSession?.carePlans?.map((plan, index) => (
              <Text key={index} style={styles.subsectionTitle}>
                {plan.name}
              </Text>
            ))}
          </View>

          <Text
            style={{
              color: "#1f7e77",
              fontSize: 20,
              marginTop: 20,
              fontWeight: 600,
              fontFamily: "Helvetica",
            }}
          >
            Goals
          </Text>
          {session?.userSession?.carePlans[0]?.goals?.map((goal, index) => (
            <View key={index} style={{ marginBottom: 20 }}>
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  marginBottom: 10,
                  paddingTop: 20,
                }}
              >
                <View style={{ flexDirection: "row", gap: 10 }}>
                  <Text
                    style={{
                      ...styles.listItem,
                      marginLeft: 0,
                      fontWeight: 600,
                      fontFamily: "Helvetica",
                    }}
                  >
                    {index + 1 < 10 ? `0${index + 1}` : index + 1}
                  </Text>
                  {/* <Text style={{ ...styles.listItem,marginLeft:0, fontWeight: 600,fontFamily:"DejaVu Sans" }}>{index + 1 < 10 ? `0${index + 1}` : index + 1}</Text> */}
                  <Text style={{ ...styles.listItem, fontWeight: 600 }}>
                    {goal.goalName}
                  </Text>
                </View>
                <View style={{ flexDirection: "row", gap: 10 }}>
                  <Text
                    style={{ fontSize: 18, color: "#1f7e77", fontWeight: 600 }}
                  >
                    Duration
                  </Text>
                  <Text style={{ fontSize: 18, fontWeight: 600 }}>
                    {goal.duration} Days
                  </Text>
                </View>
              </View>

              <View style={{}}>
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    marginBottom: 10,
                  }}
                >
                  <View>
                    <Text
                      style={{
                        ...styles.listItem,
                        fontWeight: 600,
                        marginBottom: 20,
                        marginLeft: 0,
                      }}
                    >
                      Tasks
                    </Text>
                    {goal.tasks?.map((task, taskIndex) => (
                      <Text
                        key={taskIndex}
                        style={{
                          ...styles.listItem,
                          color: "#666",
                          marginBottom: 10,
                          marginLeft: 0,
                        }}
                      >
                        {task.title}
                      </Text>
                    ))}
                  </View>
                  <View>
                    <Text
                      style={{
                        ...styles.listItem,
                        fontWeight: 600,
                        marginBottom: 20,
                      }}
                    >
                      Frequency
                    </Text>
                    {goal.tasks?.map((task, taskIndex) => (
                      <Text
                        key={taskIndex}
                        style={{
                          ...styles.listItem,
                          color: "#666",
                          marginBottom: 10,
                        }}
                      >
                        {task.frequency}
                      </Text>
                    ))}
                  </View>
                  <View>
                    <Text
                      style={{
                        ...styles.listItem,
                        fontWeight: 600,
                        marginBottom: 20,
                      }}
                    >
                      Priority
                    </Text>
                    {goal.tasks?.map((task, taskIndex) => (
                      <Text
                        key={taskIndex}
                        style={{
                          ...styles.listItem,
                          color: "#666",
                          marginBottom: 10,
                        }}
                      >
                        {task.priority}
                      </Text>
                    ))}
                  </View>
                </View>
              </View>
            </View>
          ))}

          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              marginTop: 20,
            }}
          >
            <View style={{ width: "48%" }}>
              <View style={{ flexDirection: "row" }}>
                <Image src="images/Medications.png" style={{ width: 18, height: 18 }} />
                <Text style={{ ...styles.listItem, color: "#1f7e77" }}>
                  Medications
                </Text>
              </View>

              {session?.userSession?.carePlans[0]?.medications?.map(
                (med, index) => (
                  <View
                    key={index}
                    style={{ flexDirection: "row", gap: 10, marginTop: 10 }}
                  >
                    <Text style={styles.listItem}>
                      {med.medicineName.label}
                    </Text>
                    <Text style={styles.listItem}>{med.quantity}</Text>
                    <Text style={styles.listItem}>{med.schedule}</Text>
                  </View>
                )
              )}
            </View>
            <View style={{ width: "48%" }}>
              <View style={{ flexDirection: "row" }}>
                <Image src="images/Supplements.png" style={{ width: 25, height: 25 }} />
                <Text style={{ ...styles.listItem, color: "#1f7e77" }}>
                  Supplements
                </Text>
              </View>
              {session?.userSession?.carePlans[0]?.supplements?.map(
                (supp, index) => (
                  <View
                    key={index}
                    style={{ flexDirection: "row", gap: 10, marginTop: 10 }}
                  >
                    <Text style={styles.listItem}>
                      {supp.supplementName.label}
                    </Text>
                    <Text style={styles.listItem}>{supp.quantity}</Text>
                    <Text style={styles.listItem}>{supp.schedule}</Text>
                  </View>
                )
              )}
            </View>
          </View>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              marginTop: 20,
            }}
          >
            <View style={{ marginTop: 20 }}>
              <View style={{ flexDirection: "row" }}>
                <Image src="images/Meal-Plan.png" style={{ width: 25, height: 25 }} />
                <Text style={{ ...styles.listItem, color: "#1f7e77" }}>
                  Meal Plan
                </Text>
              </View>

              <Text style={styles.listItem}>
                {session?.userSession?.carePlans[0]?.mealPlan?.planName}
              </Text>
            </View>

            <View style={{ marginTop: 20 }}>
              <View style={{ flexDirection: "row" }}>
                <Image src="images/Fitness-Plan.png" style={{ width: 25, height: 25 }} />
                <Text style={{ ...styles.listItem, color: "#1f7e77" }}>
                  Fitness Plan
                </Text>
              </View>
              <Text style={styles.listItem}>
                {session?.userSession?.carePlans[0]?.fitnessPlan?.planName}
              </Text>
            </View>

            <View style={{ marginTop: 20 }}>
              <View style={{ flexDirection: "row" }}>
                <Image src="images/Affirmations.png" style={{ width: 25, height: 25 }} />
                <Text style={{ ...styles.listItem, color: "#1f7e77" }}>
                  Affirmations
                </Text>
              </View>
              {session?.userSession?.carePlans[0]?.affirmations?.map(
                (affirmation, index) => (
                  <Text key={index} style={styles.listItem}>
                    {affirmation}
                  </Text>
                )
              )}
            </View>
          </View>

          <View style={{ marginTop: 20 }}>
            {stripHtml(session?.userSession?.carePlanComments)}
          </View>
        </View>
        </PageWithHeader>
    </Document>
  );
};

export default SessionNotePDF;
